import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { getRequestErrorMessage } from '@/utils/requestError';

import { TableEmptyState } from '../TableEmptyState';
import { RetryButton } from './CardErrorState.styled';

export interface CardErrorStateProps {
  title?: string;
  description?: string;
  /** Surowy błąd — zamieniamy go na komunikat zrozumiały dla użytkownika. */
  error?: unknown;
  onRetry?: () => void;
  showRetry?: boolean;
}

/**
 * Stan błędu karty/wykresu. Wygląda jak pusty stan (ta sama ilustracja,
 * typografia i odstępy), bo dla użytkownika to ten sam moment: „nie ma czego
 * pokazać". Różnica jest w treści i w przycisku ponowienia.
 *
 * Świadomie NIE pokazujemy surowych komunikatów w rodzaju „Request failed with
 * status code 500" — z nimi użytkownik nie zrobi nic poza zdziwieniem.
 */
export const CardErrorState: FC<CardErrorStateProps> = ({
  title,
  description,
  error,
  onRetry,
  showRetry = true,
}) => {
  const { t } = useTranslation();

  const resolvedTitle = title || String(t('common.loadingError', 'Nie udało się wczytać danych'));
  const resolvedDescription = description
    || getRequestErrorMessage(error, String(t('common.failedToLoadData', 'Spróbuj ponownie za chwilę.')));

  return (
    <TableEmptyState
      variant="notFound"
      title={resolvedTitle}
      description={resolvedDescription}
      action={showRetry && onRetry ? (
        <RetryButton type="button" onClick={onRetry}>
          {t('common.tryAgain', 'Spróbuj ponownie')}
        </RetryButton>
      ) : undefined}
    />
  );
};
